import { forceRefreshToken, getProviderUserId, isKindeSession } from 'library/auth/authProvider';
import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { authActions } from 'reduxStore/auth/slice';
import { Notification } from 'zComponents/atoms';
import { organisationActions } from './slice';

export function* getOrganisations() {
	try {
		// Dynamically import superFetch
		const { default: superFetch } = yield call(() => import('library/helpers/superFetch'));

		// The backend derives the authoritative actor from the verified token (the orgByPerson
		// IDOR fix) and IGNORES ?fbPersonID for a Kinde session, so we send no query param and
		// rely on the access token. (The orgByPerson schema cap was also relaxed 32→255 in both
		// routesV4/V5, so a ~35-char Kinde sub no longer overflows — but omitting the param is
		// still correct: it's server-ignored regardless.) Presence is confirmed by isKindeSession,
		// which is access-token based
		// and therefore reliable across a hard-refresh token renewal — unlike getProviderUserId,
		// which reads the Kinde ID token via getUser() and can be momentarily empty right after a
		// refresh (that was the "No FBPersonID found" on Ctrl+Shift+R).
		const kindeSession = yield call(isKindeSession);

		let queryParam;
		if (kindeSession) {
			queryParam = {};
		} else {
			// Firebase: send the uid for query-param parity + guard on session presence.
			const fBPersonID = yield call(getProviderUserId);
			if (!fBPersonID) {
				throw new Error('No FBPersonID found in the current profile');
			}
			queryParam = { fbPersonID: fBPersonID };
		}

		// Fetch organisation details.
		//
		// KINDE → the ORG-LESS bootstrap `GET /v5/organisations/orgByPerson`. This call happens
		// before the user's orgs are known, so it cannot carry a real orgID; the Firebase path
		// historically passed a placeholder (`overrideOrgID = 1`). For Kinde that placeholder is
		// actively harmful: the backend resolves the per-org `kinde-auth` flag from the orgID in the
		// URL, so a placeholder means enabling Kinde for the user's REAL org can never let them log
		// in, while enabling the placeholder org would enable Kinde for everyone. The org-less route
		// (allowlisted for identity-only Kinde auth server-side) is what makes per-org enablement
		// real. It returns each org with a `kindeEnabled` marker — see the filter below.
		//
		// FIREBASE → unchanged: the org-scoped route with the placeholder org.
		// ? Using the override parameter to force the API to return the workspace details for the user's first time fetch.
		// ? As user login has just occurred, the workspace details are not yet fetched, we have to override the workspace fetch to get the workspaces details.
		const [fetchOrgs, fetchArgs] = kindeSession
			? [superFetch.getWithoutOrg, ['/organisations/orgByPerson', {}, queryParam]]
			: [superFetch.get, ['/organisations/orgByPerson', {}, queryParam, 1]];

		let response = yield call(fetchOrgs, ...fetchArgs);

		// This first bootstrap call right after login can race a not-yet-fresh cached token → 401.
		// Force a token renewal and retry ONCE so the user doesn't have to hard-refresh; a genuinely
		// invalid token still 401s on the retry and falls through to the error below.
		if (response?.error?.statusCode === 401 || response?.statusCode === 401) {
			yield call(forceRefreshToken);
			response = yield call(fetchOrgs, ...fetchArgs);
		}

		// Handle response
		if (response.statusCode !== 200) {
			throw new Error(response.error?.message || 'Failed to fetch workspaces');
		}

		const organisations = response.data ?? [];

		if (kindeSession) {
			// Only route into orgs where Kinde is actually enabled. Every subsequent call is
			// org-scoped and re-gated per org, so entering a `kindeEnabled: false` org would put the
			// dashboard into a guaranteed-401 loop. Filtering here is what keeps a mixed-org user
			// (org A on Kinde, org B not) working instead of stuck.
			const kindeOrgs = organisations.filter((org) => org.kindeEnabled);

			// Belongs to orgs, but none of them are on Kinde yet. This must NOT fall through as an
			// empty list: an empty list is the brand-new-user signal that leads to org creation, and
			// spawning an empty org for an existing member would be a real data problem. Fail with a
			// clear message instead — they can still sign in with their existing credentials.
			if (organisations.length > 0 && kindeOrgs.length === 0) {
				throw new Error(
					'Kinde sign-in is not enabled for your organisation yet. Please sign in with your email and password.'
				);
			}

			yield put(organisationActions.loadOrganisationsSuccess(kindeOrgs));
			return;
		}

		// Dispatch success action with retrieved data
		yield put(organisationActions.loadOrganisationsSuccess(organisations));
	} catch (error) {
		// Dispatch error action with the error message
		yield put(organisationActions.loadOrganisationsError(error.message));
	}
}

export function* getOrganisationsSuccess() {
	// Do something after the workspaces have been successfully loaded

	// Get the selected profile
	yield put(authActions.getUserProfile());
}

export function* deleteOrganisationSaga({ payload }) {
	const { deletedOrgID } = payload;

	try {
		// Call the API to delete the organisation
		yield SuperFetch.delete(`/organisations`);
		// Dispatch success action
		yield put(organisationActions.deleteOrganisationSuccess({ deletedOrgID }));
		Notification('success', 'Organisation deleted successfully.');
	} catch (error) {
		yield put(organisationActions.deleteOrganisationError(error.message));
		Notification('error', 'Failed to delete organisation. Please try again later.');
	}
}

export function* deleteOrganisationSuccess({ payload }) {
	const { deletedOrgID } = payload;

	// Get the current list of organisations from the store
	const { data } = yield select((state) => state.Organisations);

	if (!data || data.length === 0) {
		// No organisations left — fetch fresh user profile
		yield put(authActions.getUserProfile());
		return;
	}

	// Find the index of the organisation that was just deleted
	const indexDeleted = data.findIndex((org) => org.orgID === deletedOrgID);

	// If there was only one organisation and it was deleted
	if (data.length === 1 && indexDeleted !== -1) {
		yield put(authActions.getUserProfile());
		return;
	}

	// If the deleted organisation was the first one in the list
	if (indexDeleted === 0 && data.length > 1) {
		// Switch to the second organisation
		yield put(authActions.switchProfile(data[1].orgID));
	} else {
		// Otherwise, switch to the first organisation
		yield put(authActions.switchProfile(data[0].orgID));
	}
}
export function* updateOrganisationSaga({ payload }) {
	const { orgID, values } = payload;
	try {
		const response = yield SuperFetch.patch(`/organisations/${orgID}`, values);

		// Check if the response indicates an error
		if (response.statusCode === 200) {
			Notification('success', 'Organisation updated successfully.');
			yield put(organisationActions.updateOrganisationSuccess({ orgID, data: values }));
		} else {
			throw Error(response.error?.message || response.error || 'Failed to update device field.');
		}
	} catch (error) {
		yield put(organisationActions.updateOrganisationError(error.message));
		Notification('error', 'Failed to update organisation. Please try again later.');
	}
}

// The main export for the saga
export default function* settingsSagas() {
	yield takeLatest(organisationActions.loadOrganisations, getOrganisations);
	yield takeLatest(organisationActions.loadOrganisationsSuccess, getOrganisationsSuccess);
	yield takeLatest(organisationActions.deleteOrganisation, deleteOrganisationSaga);
	yield takeLatest(organisationActions.deleteOrganisationSuccess, deleteOrganisationSuccess);
	yield takeLatest(organisationActions.updateOrganisation, updateOrganisationSaga);
}
// This is the main saga that will be exported
